// Channels.js
import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  useMediaQuery,
  useTheme,
  Alert,
} from "@mui/material";
import { useChannels } from "../../../hooks/useChannels";
import ChannelTable from "../../../components/ChannelTable";
import AddChannelForm from "../../../components/AddChannelForm";
import { usePageContext } from "../../../context/PageContext";
import { CHANNEL_TYPES } from "../../../constants/channelCatalog";
import { useTranslation } from "react-i18next";

const Channels = () => {
  const { t } = useTranslation("settings");
  const autoOpenedRef = useRef(false);
  const { currentPage, setCurrentPage } = usePageContext();
  const [pageSize, setPageSize] = useState(50);
  const [search, setSearch] = useState("");
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const {
    data,
    error,
    refetch,
    updateMutation,
    deleteMutation,
    isLoading,
  } = useChannels(currentPage, pageSize, search);

  useEffect(() => {
    const list = data?.channels || [];
    const hasSearchFilter = typeof search === "string" && search.trim() !== "";
    const reallyEmpty = !hasSearchFilter && list.length === 0;
    if (!isLoading && !selectedChannel && reallyEmpty && !autoOpenedRef.current) {
      autoOpenedRef.current = true;
      setSelectedChannel({}); // novo canal
    }
  }, [isLoading, data, selectedChannel, search]);

  useEffect(() => {
    if (error) {
      setErrorMessage("Erro ao carregar canais. Por favor, tente novamente.");
    } else {
      setErrorMessage(null);
    }
  }, [error]);

  const handleEditChannel = (channel) => {
    setSelectedChannel(channel);
    setErrorMessage(null);
  };

  const handleDeleteChannel = (id) => {
    setConfirmDelete(id);
    setErrorMessage(null);
  };

  const confirmDeleteChannel = () => {
    if (!confirmDelete) return;

    setIsProcessing(true);
    deleteMutation.mutate(confirmDelete, {
      onSuccess: () => {
        setConfirmDelete(null);
        refetch();
      },
      onError: (error) => {
        setErrorMessage(
          error.response?.data?.message || "Erro ao excluir o canal."
        );
      },
      onSettled: () => {
        setIsProcessing(false);
      },
    });
  };

  const handlePageChange = (newPage) => setCurrentPage(newPage + 1);
  const handlePageSizeChange = (newPageSize) => setPageSize(newPageSize);

  const handleSearch = (searchTerm) => {
    setSearch(searchTerm);
    refetch();
  };

  const handleCloseForm = () => {
    setSelectedChannel(null);
    setErrorMessage(null);
  };

  const handleToggleStatus = (channel) => {
    if (!channel?.id) return;
    const newStatus = Number(channel.status) === 1 ? 0 : 1;
    setIsProcessing(true);
    updateMutation.mutate(
      { ...channel, status: newStatus },
      {
        onSuccess: () => refetch(),
        onError: (error) => {
          setErrorMessage(
            error.response?.data?.message || "Erro ao alterar status do canal."
          );
        },
        onSettled: () => setIsProcessing(false),
      }
    );
  };

  const handleDeleteSelected = async (ids) => {
    if (!ids?.length) return;
    setIsProcessing(true);
    setErrorMessage(null);
    const results = await Promise.allSettled(
      ids.map((id) => deleteMutation.mutateAsync(id))
    );
    const failed = results.filter((r) => r.status === "rejected");
    if (failed.length > 0) {
      setErrorMessage(
        failed.length === ids.length
          ? "Erro ao excluir os canais."
          : `${failed.length} de ${ids.length} exclusão(ões) falharam.`
      );
    }
    refetch();
    setIsProcessing(false);
  };

  return (
    <Box sx={{ paddingTop: 1 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isMobile ? "flex-start" : "center",
          mb: 2,
          gap: isMobile ? 2 : 0,
        }}
      >
        <Typography variant="h4" gutterBottom>
          {t("channelsPage.headingChannels")}
        </Typography>
        {!selectedChannel && <Button
          variant="contained"
          color="primary"
          fullWidth={isMobile}
          onClick={() => setSelectedChannel({})}
        >
          {t("channelsPage.addChannel")}
        </Button>}
      </Box>

      {errorMessage && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{errorMessage}</Alert>
        </Box>
      )}

      {selectedChannel ? (
        <AddChannelForm
          key={selectedChannel?.id != null ? String(selectedChannel.id) : "new-channel"}
          channel={selectedChannel}
          isLoading={isLoading || isProcessing}
          onClose={handleCloseForm}
          onSubmit={(updatedChannel) => {
            setIsProcessing(true);
            updateMutation.mutate(updatedChannel, {
              onSuccess: (result) => {
                const createdId = result?.data?.id;

                if (!updatedChannel.id && createdId) {
                  // Inclusão:
                  // - Para canais de e-mail (SES) COM domínio próprio configurado,
                  //   mantemos o formulário aberto para permitir acompanhar o status
                  //   e instruções de DNS logo após o primeiro save.
                  // - Para canais de WhatsApp QRCode (tipo 6), mantemos o formulário aberto
                  //   para permitir criar instância e exibir QRCode logo após o save.
                  // - Para canais que usam somente o domínio padrão da Xpro
                  //   (sem email_domain preenchido) e para os demais tipos de canal,
                  //   voltamos imediatamente para a lista (sem exigir um segundo "Salvar").
                  if (
                    Number(updatedChannel.tipo) === CHANNEL_TYPES.EMAIL_AWS_SES &&
                    updatedChannel.email_domain
                  ) {
                    setSelectedChannel({
                      ...updatedChannel,
                      id: createdId,
                    });
                    refetch();
                    return;
                  }

                  if (Number(updatedChannel.tipo) === CHANNEL_TYPES.WHATSAPP_QR) {
                    setSelectedChannel({
                      ...updatedChannel,
                      id: createdId,
                    });
                    refetch();
                    return;
                  }

                  refetch();
                  handleCloseForm();
                  return;
                }

                // Edição: comportamento atual (volta para a lista)
                refetch();
                handleCloseForm();
              },
              onError: (error) => {
                // Tenta expor a mensagem específica do backend
                const raw = error?.data || error?.response?.data || error || {};
                const serverMessage =
                  (typeof raw.message === "string" && raw.message) ||
                  (typeof raw.data === "string" && raw.data) ||
                  (typeof raw.error === "string" && raw.error) ||
                  null;

                let friendly = serverMessage || "Erro ao salvar o canal.";

                if (typeof serverMessage === "string") {
                  if (serverMessage.includes("Column 'id_projeto'")) {
                    friendly =
                      "Selecione um Projeto para este canal. O projeto é obrigatório para salvar o canal.";
                  } else if (serverMessage.includes("Column 'id_tipo'")) {
                    friendly =
                      "Selecione um Tipo padrão em \"Roteamento padrão\". Esse tipo é obrigatório para salvar o canal.";
                  }
                }

                setErrorMessage(
                  friendly
                );
              },
              onSettled: () => {
                setIsProcessing(false);
              },
            });
          }}
        />
      ) : (
        <ChannelTable
          channels={data?.channels || []}
          rowCount={data?.pagination?.total || 0}
          pageSize={pageSize}
          page={currentPage - 1}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onSearch={handleSearch}
          isLoading={isLoading || isProcessing}
          onEditChannel={handleEditChannel}
          onDeleteChannel={handleDeleteChannel}
          onDeleteSelected={handleDeleteSelected}
          onToggleStatus={handleToggleStatus}
        />
      )}

      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Confirmação de Exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            {isProcessing
              ? "Processando exclusão..."
              : "Tem certeza de que deseja excluir este canal?"}
          </Typography>
        </DialogContent>
        <DialogActions>
          {!isProcessing && (
            <>
              <Button onClick={() => setConfirmDelete(null)}>Cancelar</Button>
              <Button color="error" onClick={confirmDeleteChannel}>
                Excluir
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Channels;
